import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { activityService } from "@/services/activity.service";
import RegistrationModal from "@/pages/home/components/RegistrationModal";

const typeNames: Record<string, string> = {
  webinar: "וובינר",
  training: "הכשרה",
  workshop: "סדנאות",
};
const typeColors: Record<string, string> = { webinar: "#EDE9FE", training: "#FEF9C3", workshop: "#FEE2E2" };
const tagColors: Record<string, string> = { webinar: "#A78BFA", training: "#FDE68A", workshop: "#FCA5A5" };

export default function AllActivitiesPage() {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterType = searchParams.get("type") || "";

  useEffect(() => {
    setLoading(true);
    activityService.list().then((all) => {
      if (filterType) {
        setActivities(all.filter((a: any) => a.type?.name === filterType));
      } else {
        setActivities(all);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [filterType]);

  const title = filterType ? `${typeNames[filterType] || filterType}` : "כל הפעילויות";

  return (
    <div className="min-h-screen pb-8" style={{ background: "#FAF9F6", direction: "rtl" }} dir="rtl">
      <div className="sticky top-0 z-20 flex items-center justify-between px-4 py-3 border-b-2 border-gray-900 bg-white">
        <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-gray-900 bg-white hover:bg-gray-50 cursor-pointer">
          <i className="ri-home-5-line text-lg" />
        </button>
        <h1 className="text-lg font-black text-gray-900">{title}</h1>
        <div className="w-16" />
      </div>

      <div className="px-4 pt-4">
        <p className="text-xs text-gray-500 mb-3">
          {loading ? "טוען פעילויות..." : `${activities.length} פעילויות`}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {loading
            ? [0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border-[2.5px] border-gray-900 overflow-hidden"
                >
                  <div className="h-32 bg-gray-200 animate-pulse" />
                  <div className="p-4 flex flex-col gap-3">
                    <div className="h-4 w-3/4 rounded bg-gray-200 animate-pulse" />
                    <div className="h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
                    <div className="h-3 w-2/3 rounded bg-gray-100 animate-pulse" />
                    <div className="h-10 w-full rounded-xl bg-gray-200 animate-pulse" />
                  </div>
                </div>
              ))
            : activities.map((act) => (
            <div key={act.id} className="bg-white rounded-2xl border-[2.5px] border-gray-900 overflow-hidden">
              <div className="relative h-32 overflow-hidden">
                <img src={act.image || ""} alt={act.title} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <span className="absolute top-2 right-2 text-xs font-black px-2 py-0.5 rounded-full border-2 border-gray-900 bg-white text-gray-700">
                  {typeNames[act.type?.name] || ""}
                </span>
                <span className={`absolute top-2 left-2 text-xs font-black px-2 py-0.5 rounded-full border-2 border-gray-900 ${act.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {act.status === 'active' ? 'פעיל' : 'הסתיים'}
                </span>
              </div>
              <div className="p-4 flex flex-col gap-3">
                <h3 className="text-sm font-black text-gray-900 leading-snug line-clamp-2">{act.title}</h3>
                <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                  {act.date && <span className="flex items-center gap-1"><i className="ri-calendar-line text-gray-400"></i>{act.date}</span>}
                  {act.location && <><span>|</span><span className="flex items-center gap-1"><i className="ri-map-pin-line text-gray-400"></i>{act.location}</span></>}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-group-line text-gray-400"></i>
                  <span>{act.participants_count} / {act.max_participants} משתתפים</span>
                </div>
                {act.description && <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{act.description}</p>}
                <button onClick={() => { setSelected(act); setModalOpen(true); }}
                  className="w-full rounded-xl border-2 border-gray-900 py-2.5 text-sm font-black hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                  style={{ background: typeColors[act.type?.name] || "#F3F4F6" }}>
                  הרשמה
                </button>
              </div>
            </div>
            ))}
        </div>
      </div>

      <RegistrationModal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        registerableType="Activity"
        registerableId={selected?.id}
      />
    </div>
  );
}
